import { alpha, createTheme } from '@mui/material/styles';

/** Matches app-frontend `createAppTheme('dark')` — Explorer product look. */
const DARK_BASE = '#08070B';
const DARK_PAPER = '#121016';
const WARM_MUTED = '#9E9A94';
const SURFACE_MUTED = '#17161C';

export const explorerDarkTheme = createTheme({
    palette: {
        mode: 'dark',
        primary: {
            main: '#FFFFFF',
            light: alpha('#FFFFFF', 0.14),
            dark: WARM_MUTED,
            contrastText: DARK_BASE,
        },
        secondary: {
            main: '#FF6B6B',
            light: '#FFA5A5',
            dark: '#CC5555',
        },
        background: {
            default: DARK_BASE,
            paper: DARK_PAPER,
        },
        text: {
            primary: '#F7F7F7',
            secondary: WARM_MUTED,
        },
        divider: alpha('#FFFFFF', 0.1),
        action: {
            hover: alpha('#FFFFFF', 0.08),
        },
    },
    typography: {
        fontFamily: '"Inter", "Roboto", "DM Sans", "Arial", sans-serif',
        h1: {
            fontFamily: '"DM Serif Display", serif',
            fontSize: '2.6rem',
            fontWeight: 800,
            lineHeight: 1.4,
            color: '#F7F7F7',
        },
        subtitle1: {
            fontFamily: '"DM Sans", serif',
            fontSize: '0.8125rem',
            fontWeight: 500,
        },
        body2: {
            fontSize: '0.8125rem',
        },
        button: {
            textTransform: 'none',
            fontWeight: 500,
        },
    },
    shape: {
        borderRadius: 8,
    },
    components: {
        MuiContainer: {
            styleOverrides: {
                maxWidthMd: {
                    maxWidth: '800px !important',
                },
            },
        },
        MuiPaper: {
            defaultProps: { elevation: 0 },
            styleOverrides: {
                root: {
                    backgroundImage: 'none',
                    backgroundColor: DARK_PAPER,
                    border: `1px solid ${alpha('#FFFFFF', 0.1)}`,
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    borderColor: alpha('#FFFFFF', 0.1),
                },
                head: {
                    color: WARM_MUTED,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                },
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    '&:hover': {
                        backgroundColor: alpha('#FFFFFF', 0.04),
                    },
                },
            },
        },
        MuiTableSortLabel: {
            styleOverrides: {
                root: {
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    color: WARM_MUTED,
                    '&.Mui-active': {
                        color: '#FFFFFF',
                        '& .MuiTableSortLabel-icon': {
                            color: '#FFFFFF',
                        },
                    },
                    '&:hover': {
                        color: '#FFFFFF',
                    },
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    backgroundColor: DARK_PAPER,
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha('#FFFFFF', 0.1),
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha('#FFFFFF', 0.25),
                    },
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    color: WARM_MUTED,
                    '&.Mui-focused': {
                        color: '#FFFFFF',
                    },
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    fontWeight: 600,
                },
                outlined: {
                    borderColor: alpha('#FFFFFF', 0.25),
                    color: '#F7F7F7',
                    '&:hover': {
                        borderColor: alpha('#FFFFFF', 0.4),
                        backgroundColor: alpha('#FFFFFF', 0.06),
                    },
                },
            },
        },
        MuiLinearProgress: {
            styleOverrides: {
                root: {
                    backgroundColor: alpha('#FFFFFF', 0.08),
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    color: WARM_MUTED,
                },
            },
        },
    },
});

export { SURFACE_MUTED, DARK_BASE };
